import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef
} from '@angular/core';
import { ConfirmModal } from 'angular/custom/services/confirm-modal.service';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'pkp-toggle-panel',
    template: `
        <div class="panel panel-default panel-collapsible">
            <div class="panel-heading">
                <div class="panel-title">
                    <div class="pull-right">
                        <loading *ngIf="loading"></loading>
                        <pkp-toggle
                            class="pull-right"
                            [(ngModel)]="isOpen"
                            (ngModelChange)="toggle()"
                            [disabled]="toggling || loading || toggleDisabled"
                            *ngIf="toggleVisible"
                        >
                        </pkp-toggle>
                    </div>
                    <loading *ngIf="loading"></loading>
                    <ng-content select="[titleX]"></ng-content>
                    <!-- In AngularJS world title doesn't work. -->
                    <ng-content select="[title]"></ng-content>
                </div>
                <div class="help-block row">
                    <div class="col-md-11">
                        <ng-content select="[subtitle]"></ng-content>
                    </div>
                </div>
            </div>
            <div [collapse]="isCollapsed" [isAnimated]="false">
                <!-- isAnimated set to false b/c bug when lazy loading dynamic height content -->
                <div class="panel-body">
                    <div class="row">
                        <div class="col-sm-12">
                            <ng-content select="[content]"></ng-content>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class TogglePanelComponent implements OnInit, OnChanges {
    @Output()
    isOpenChange = new EventEmitter<boolean>();

    @Output('onConfirm')
    confirmEventEmitter: EventEmitter<TogglePanelCallback> = new EventEmitter();

    @Input()
    confirmTitle: string;

    @Input()
    confirmText: string;

    @Input()
    confirmTextTemplate: TemplateRef<any>;

    @Input()
    toggleVisible: boolean = true;

    @Input()
    toggleDisabled: boolean = false;

    toggling = false;
    isCollapsed: boolean;
    _isOpen: boolean;
    loading: boolean;

    @Input() get isOpen() {
        return this._isOpen;
    }

    set isOpen(isOpen: boolean) {
        this._isOpen = isOpen;
        this.isOpenChange.emit(isOpen);
    }

    constructor(private confirmModal: ConfirmModal) {}

    ngOnInit(): void {
        if (this.confirmText != null && this.confirmTextTemplate != null) {
            throw new Error(
                'You must not use text string and text templates. What am I meant to use?!'
            );
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.toggling !== true && changes['isOpen'])
            this.isCollapsed = !changes['isOpen'].currentValue;
    }

    toggle() {
        this.toggling = true;
        const successHandlerFn = () => {
            this.loading = true;
            this.confirmEventEmitter.emit((success) => {
                success
                    ? (this.isCollapsed = !this.isCollapsed)
                    : (this.isOpen = !this.isOpen);
                this.loading = false;
                this.toggling = false;
            });
        };

        if (
            this.confirmText == null &&
            this.confirmTextTemplate == null &&
            this.confirmTitle == null
        ) {
            successHandlerFn();
            return;
        }

        const rejectionHandlerFn = () => (this.isOpen = !this.isOpen);

        this.confirmText != null
            ? this.confirmModal
                  .fromText(this.confirmText, this.confirmTitle)
                  .pipe(finalize(() => (this.toggling = false)))
                  .subscribe(successHandlerFn, rejectionHandlerFn)
            : this.confirmModal
                  .fromTemplate(this.confirmTextTemplate, this.confirmTitle)
                  .pipe(finalize(() => (this.toggling = false)))
                  .subscribe(successHandlerFn, rejectionHandlerFn);
    }
}

export type TogglePanelCallback = (success: boolean) => void;
